import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Truck } from "lucide-react";

const FIELDS = [
  ["name", "Destinatario", "col-span-2"],
  ["street1", "Indirizzo", "col-span-2"],
  ["city", "Città", ""],
  ["postalCode", "CAP", ""],
  ["state", "Provincia (facoltativa)", ""],
  ["phone", "Telefono", ""],
  ["email", "Email", "col-span-2"],
  ["weight", "Peso (kg)", ""],
  ["codValue", "Contrassegno: importo ordine (€)", ""],
  ["notes", "Note per il corriere", "col-span-2"],
];

export const COD_FEE = 9;
const num = (v) => Number(String(v ?? "").replace(",", ".")) || 0;

const EMPTY = { name: "", street1: "", city: "", state: "", postalCode: "", phone: "", email: "", weight: 1, codValue: 0, exchange: false, notes: "" };

export const openPdf = async (url, body) => {
  try {
    const res = body
      ? await api.post(url, body, { responseType: "blob", timeout: 60000 })
      : await api.get(url, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
  } catch {
    toast.error("PDF etichetta non disponibile");
  }
};

export const openGlsLabel = (orderId) => openPdf(`/gls/orders/${orderId}/label`);

// shipment: null = closed, {} = new manual label, object = edit queued shipment
export const GlsShipmentDialog = ({ shipment, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isNew = shipment && !shipment.id;

  useEffect(() => {
    if (shipment) setForm({ ...EMPTY, ...shipment });
  }, [shipment]);

  const submit = async () => {
    const missing = ["name", "street1", "city", "postalCode"].filter((k) => !String(form[k] || "").trim());
    if (missing.length) return toast.error("Compila destinatario, indirizzo, città e CAP");
    setSaving(true);
    try {
      const body = { ...form, weight: num(form.weight) || 1, codValue: num(form.codValue), exchange: !!form.exchange };
      FIELDS.forEach(([k]) => { if (body[k] === null) body[k] = ""; });
      if (isNew) await api.post("/gls/shipments", body);
      else await api.put(`/gls/shipments/${shipment.id}`, body);
      toast.success(isNew ? "Etichetta manuale aggiunta alla coda" : "Modifiche salvate");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!shipment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="gls-shipment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5" /> {isNew ? "Nuova etichetta manuale" : "Modifica etichetta"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map(([key, label, cls]) => (
            <label key={key} className={`text-xs text-gray-600 space-y-1 ${cls}`}>
              <span>{label}</span>
              <Input
                value={form[key] ?? ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`gls-input-${key}`}
              />
            </label>
          ))}
          <div className="col-span-2 text-xs rounded p-2 bg-orange-50 text-orange-800" data-testid="gls-cod-summary">
            {num(form.codValue) > 0
              ? <>Il corriere incasserà <b>€{(num(form.codValue) + COD_FEE).toFixed(2)}</b> (ordine €{num(form.codValue).toFixed(2)} + €{COD_FEE} contrassegno)</>
              : "Senza contrassegno: lascia 0 se l'ordine è già pagato"}
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer" data-testid="gls-exchange-toggle">
            <Checkbox checked={!!form.exchange} onCheckedChange={(v) => setForm({ ...form, exchange: !!v })} data-testid="gls-input-exchange" />
            <span><b>Exchange</b> – il corriere consegna e ritira un reso nello stesso momento</span>
          </label>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="bg-[#C9A227] hover:bg-[#b08d1f] text-white" data-testid="gls-save-shipment-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
